import { Paperclip, Link as LinkIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common-config/constants'

type TemplateLike = {
    name?: string;
    message?: string | null;
    isPoll?: boolean;
    sendLinkPreview?: boolean;
    linkPreview?: { url?: string | null } | null;
    asset?: { type?: string | null; name?: string | null } | null;
    poll?: { question?: string; options?: Array<{ text: string }> } | null;
};

export default function TemplateRowDetails({ template: _template }: { template?: object }) {
    const template = _template as TemplateLike | undefined;
    const { t } = useTranslation(namespace);
    // Assuming InsertLinkRounded and AttachFileRounded are imported components/icons
    const isLinkSwitch = !!template?.sendLinkPreview;
    const hasLink = !!template?.linkPreview?.url;
    const hasFiles = !!template?.asset;
    const isEmptyMessage = !template?.message;
    const isTemplateName = !!template?.name;


    if (!isLinkSwitch && !hasLink && !hasFiles && !isTemplateName && isEmptyMessage && !template?.isPoll)
        return null;

    return (
        <>
            <div className="flex flex-row justify-start items-center gap-3">
                {template?.name && (
                    <span className="text-[17px] font-semibold text-[var(--color-text)] dark:text-[var(--color-text)]">
                        {template.name}
                    </span>
                )}
                <div className="flex items-center gap-2">
                    {(hasLink && isLinkSwitch) && (
                        <div className={cn(
                            "rounded-md px-2 py-0.5",
                            "text-[#38bf62] bg-[#e1f4df] dark:bg-[#e1f4df]",
                            "text-xs font-medium box-border",
                            "flex items-center gap-1"
                        )}>
                            <LinkIcon className="h-3 w-3" />
                            <span>{t('campaign.link')}</span>
                        </div>
                    )}
                    {hasFiles && (
                        <div className={cn(
                            "rounded-md px-2 py-0.5",
                            "text-[#38bf62] bg-[#e1f4df] dark:bg-[#e1f4df]",
                            "text-xs font-medium box-border",
                            "flex items-center gap-1"
                        )}>
                            <Paperclip className="h-3 w-3" />
                            <span>{t('campaign.file')}</span>
                        </div>
                    )}
                </div>
            </div>
            {!isEmptyMessage && !template.isPoll && (
                <div className={cn(
                    "text-sm font-normal text-slate-600 dark:text-slate-400",
                    "whitespace-nowrap overflow-hidden text-ellipsis",
                    "max-w-[calc(100%-40px)] mt-1"
                )}>
                    {template.message}
                </div>
            )}
            {isEmptyMessage && !template.isPoll && !template?.asset?.type?.includes('audio') && (
                <div className={cn(
                    "text-sm font-normal text-slate-400 dark:text-slate-500 italic",
                    "whitespace-nowrap overflow-hidden text-ellipsis",
                    "max-w-[calc(100%-40px)] mt-1"
                )}>
                    {t('templates.noMessage')}
                </div>
            )}
            {template?.asset?.type?.includes('audio') && (
                <div className={cn(
                    "text-sm font-semibold whitespace-nowrap overflow-hidden text-ellipsis",
                    "max-w-[calc(100%-40px)]",
                    "text-[var(--color-text)] dark:text-[var(--color-text)]"
                )}>
                    {template?.asset?.name}
                </div>
            )}
            {template?.isPoll && (
                <div className={cn(
                    "text-sm font-semibold whitespace-nowrap overflow-hidden text-ellipsis",
                    "max-w-[calc(100%-40px)]",
                    "text-[var(--color-text)] dark:text-[var(--color-text)]"
                )}>
                    {t('campaign.poll', { question: template?.poll?.question })}<br />
                    {template?.poll?.options
                        ?.filter((option) => option.text !== '')
                        .map((option, index) => (
                            <span key={index}>
                                {`${index}) ${option.text}`} <br />
                            </span>
                        ))}
                </div>
            )}
        </>
    );
}
